import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { PMDialog } from "../../utils/PMDialog";
import { PMButton } from "../../utils/PMButton";
import { WORK_ORDER_STATUS_INCLUSION } from "./utils";
import {
  isPromangApiErrorResponse,
  promangApiErrorsMessage,
  workOrderAdminSetStatus,
} from "../../../api/work-order";

/** Statuses that imply the work order is completed for billing / hero. */
const COMPLETION_LIKE_STATUSES = [
  "finished",
  "processing",
  "processed",
  "ready_invoice",
  "invoiced",
  "approved",
];

/**
 * Admin-only: override work order status (e.g. mark legacy rows as invoiced).
 */
export function WorkOrderAdminSetStatusDialog({
  open,
  onClose,
  workOrder,
  onSaved,
}) {
  const { t } = useTranslation();
  const [status, setStatus] = useState("invoiced");
  const [markCompleted, setMarkCompleted] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const showCompletedToggle = COMPLETION_LIKE_STATUSES.includes(status);

  useEffect(() => {
    if (open && workOrder) {
      setStatus(
        workOrder.status && WORK_ORDER_STATUS_INCLUSION.includes(workOrder.status)
          ? workOrder.status
          : "invoiced"
      );
      setMarkCompleted(true);
    }
  }, [open, workOrder?.id, workOrder?.status]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!workOrder?.id || !status) return;
    setSubmitting(true);
    try {
      const res = await workOrderAdminSetStatus(workOrder.id, {
        status,
        is_completed: showCompletedToggle ? markCompleted : undefined,
      });
      if (isPromangApiErrorResponse(res)) {
        toast.error(
          promangApiErrorsMessage(
            res,
            t("work_order.admin_set_status.error")
          )
        );
        return;
      }
      const data = res?.data?.data ?? res?.data;
      toast.success(t("work_order.admin_set_status.success"));
      onSaved?.(data);
      onClose?.();
    } catch (err) {
      const res = err?.response ?? err;
      toast.error(
        promangApiErrorsMessage(res, t("work_order.admin_set_status.error"))
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title={t("work_order.admin_set_status.title")}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-gray-600">
          {t("work_order.admin_set_status.intro")}
        </p>
        <div>
          <label
            htmlFor="wo-admin-status"
            className="block text-sm font-medium text-gray-700"
          >
            {t("work_order.admin_set_status.status_label")}
          </label>
          <select
            id="wo-admin-status"
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            {WORK_ORDER_STATUS_INCLUSION.map((s) => (
              <option key={s} value={s}>
                {t(`work_order.status.${s}`, { defaultValue: s })}
              </option>
            ))}
          </select>
        </div>
        {showCompletedToggle ? (
          <label className="flex items-center gap-2 text-sm text-gray-800">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-400 text-teal-600 focus:ring-teal-500"
              checked={markCompleted}
              onChange={(e) => setMarkCompleted(e.target.checked)}
            />
            {t("work_order.admin_set_status.mark_completed")}
          </label>
        ) : null}
        <div className="flex justify-end gap-2 pt-2">
          <PMButton
            type="button"
            className="bg-gray-200 text-gray-800 hover:bg-gray-300"
            onClick={onClose}
            disabled={submitting}
          >
            {t("Cancel", { defaultValue: "Avbryt" })}
          </PMButton>
          <PMButton type="submit" disabled={submitting}>
            {submitting
              ? t("Saving", { defaultValue: "Lagrer…" })
              : t("work_order.admin_set_status.submit")}
          </PMButton>
        </div>
      </form>
    </PMDialog>
  );
}
